import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface TimelineStage {
  day: string;
  title: string;
  subtitle: string;
  description: string;
  details: string[];
  icon: string;
  color: string;
}

const stages: TimelineStage[] = [
  {
    day: 'Day 0',
    title: 'Initial Inquiry',
    subtitle: 'Share your requirements',
    description: 'Fill out our quick intake form or book a call. Tell us about your tech stack, team size, timeline, and project goals.',
    details: [
      'Technology stack & seniority requirements',
      'Project duration & start date',
      'Team size & engagement model',
      'Budget range & location preferences',
    ],
    icon: 'ri-send-plane-line',
    color: 'bg-primary-500',
  },
  {
    day: 'Days 1–2',
    title: 'Discovery Call',
    subtitle: 'Deep-dive into your needs',
    description: 'Our delivery lead joins you for a 30-minute call to align on technical needs, culture fit, and success criteria.',
    details: [
      'Technical architecture review',
      'Team culture & communication style',
      'Success metrics & KPIs definition',
      'Timezone & working hours alignment',
    ],
    icon: 'ri-video-chat-line',
    color: 'bg-primary-500',
  },
  {
    day: 'Days 3–5',
    title: 'Talent Sourcing',
    subtitle: 'Matching from our talent pool',
    description: 'We scan our pre-vetted network of 50,000+ engineers and actively recruit specialists for your exact tech stack.',
    details: [
      'AI-powered talent matching algorithm',
      'Manual screening by domain experts',
      'Availability & commitment verification',
      'Portfolio & code sample review',
    ],
    icon: 'ri-search-line',
    color: 'bg-primary-500',
  },
  {
    day: 'Days 5–7',
    title: 'Candidate Shortlist',
    subtitle: 'Top 3–5 profiles presented',
    description: 'Receive detailed profiles with resumes, skill matrices, past project summaries, and video introductions.',
    details: [
      'Detailed skill assessment scores',
      'Video introduction from each candidate',
      'Past project case studies',
      'Reference checks completed',
    ],
    icon: 'ri-stack-line',
    color: 'bg-primary-500',
  },
  {
    day: 'Days 7–10',
    title: 'Client Interviews',
    subtitle: 'You interview the candidates',
    description: 'Schedule technical and cultural interviews directly with your shortlisted engineers. We handle all logistics.',
    details: [
      'Technical deep-dive interviews',
      'Live coding sessions if desired',
      'Cultural fit assessment',
      'Realtime availability for scheduling',
    ],
    icon: 'ri-user-voice-line',
    color: 'bg-primary-500',
  },
  {
    day: 'Days 10–12',
    title: 'Final Selection',
    subtitle: 'Choose your engineer',
    description: 'Pick your ideal candidate. We can also arrange trial periods or additional evaluations if you need more confidence.',
    details: [
      'No-obligation 1-week trial option',
      'Side-by-side candidate comparison',
      'Second interview rounds available',
      'Immediate replacement if needed',
    ],
    icon: 'ri-checkbox-circle-line',
    color: 'bg-primary-500',
  },
  {
    day: 'Days 12–14',
    title: 'Contract & Setup',
    subtitle: 'Paperwork & environment prep',
    description: 'We draft contracts, set up secure communication channels, configure dev environments, and brief your new engineer.',
    details: [
      'MSA & SOW contracts signed digitally',
      'Slack/Teams channel integration',
      'VPN, repo access & dev env setup',
      'Project kickoff meeting scheduled',
    ],
    icon: 'ri-file-shield-line',
    color: 'bg-primary-500',
  },
  {
    day: 'Day 14',
    title: 'Engineer Onboarded',
    subtitle: 'Your engineer starts working',
    description: 'Your dedicated engineer joins your team fully integrated. Average time from inquiry to start: just 14 days.',
    details: [
      'Daily standups with your team',
      'Dedicated account manager check-ins',
      'Weekly performance reviews',
      '30/60/90-day success milestones',
    ],
    icon: 'ri-rocket-line',
    color: 'bg-accent-500',
  },
];

export default function ProjectTimeline() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section id="project-timeline" className="py-16 md:py-24 bg-primary-50/50 relative overflow-hidden" ref={ref}>
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-time-line text-primary-600"></i>
            <span className="text-sm font-medium text-gray-700">Typical Engagement Timeline</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            From Inquiry to
            <br />
            <span className="text-primary-600">Onboarding</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            See exactly how we take you from first contact to a fully onboarded engineer in just 14 days.
          </motion.p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Center Line - Desktop */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2">
            <motion.div
              initial={{ height: '0%' }}
              animate={inView ? { height: '100%' } : {}}
              transition={{ duration: 2, ease: 'easeInOut' }}
              className="w-full bg-primary-300"
            ></motion.div>
          </div>

          {/* Left Line - Mobile/Tablet */}
          <div className="lg:hidden absolute left-6 md:left-8 top-0 bottom-0 w-0.5">
            <motion.div
              initial={{ height: '0%' }}
              animate={inView ? { height: '100%' } : {}}
              transition={{ duration: 2, ease: 'easeInOut' }}
              className="w-full bg-primary-300"
            ></motion.div>
          </div>

          {/* Timeline Items */}
          <div className="space-y-8 md:space-y-12">
            {stages.map((stage, index) => {
              const isLeft = index % 2 === 0;
              const isExpanded = expandedIndex === index;
              const isHovered = hoveredIndex === index;
              const isLast = index === stages.length - 1;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ ...springTransition, duration: 0.7, delay: index * 0.12 }}
                  className={`relative flex items-start ${
                    isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } flex-row`}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {/* Content Card */}
                  <div className={`flex-1 lg:w-5/12 ${
                    isLeft ? 'lg:pr-12 lg:text-right' : 'lg:pl-12 lg:text-left'
                  } pl-16 md:pl-20 lg:pl-0`}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      onClick={() => toggleExpand(index)}
                      className={`bg-white rounded-xl p-5 md:p-6 shadow-sm border cursor-pointer transition-all duration-300 ${
                        isExpanded
                          ? 'border-primary-400 shadow-lg shadow-primary-500/10'
                          : 'border-gray-100 hover:border-primary-200 hover:shadow-md'
                      }`}
                    >
                      {/* Day Badge */}
                      <div className={`flex items-center gap-2 mb-3 ${
                        isLeft ? 'lg:justify-end' : 'lg:justify-start'
                      } justify-start`}>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
                          {stage.day}
                        </span>
                        {isLast && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-accent-500/10 text-accent-600 border border-accent-500/20">
                            <i className="ri-flashlight-line mr-1"></i>
                            Go Live
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-lg md:text-xl font-bold text-navy-900 mb-1">
                        {stage.title}
                      </h3>
                      <p className="text-sm font-medium text-primary-600 mb-2">
                        {stage.subtitle}
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {stage.description}
                      </p>

                      {/* Expand Indicator */}
                      <div className={`flex items-center mt-3 text-xs font-medium transition-colors ${
                        isLeft ? 'lg:justify-end' : 'lg:justify-start'
                      } justify-start`}>
                        <span className={`flex items-center gap-1 transition-colors ${
                          isExpanded ? 'text-primary-600' : 'text-gray-400 hover:text-primary-600'
                        }`}>
                          {isExpanded ? (
                            <>
                              <i className="ri-arrow-up-s-line"></i>
                              Show Less
                            </>
                          ) : (
                            <>
                              <i className="ri-arrow-down-s-line"></i>
                              Click to Expand
                            </>
                          )}
                        </span>
                      </div>

                      {/* Expanded Details */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className="overflow-hidden"
                          >
                            <div className={`mt-4 pt-4 border-t border-gray-100 ${
                              isLeft ? 'lg:text-right' : 'lg:text-left'
                            }`}>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                What happens at this stage:
                              </p>
                              <ul className={`space-y-2 ${
                                isLeft ? 'lg:items-end' : 'lg:items-start'
                              } flex flex-col items-start`}>
                                {stage.details.map((detail, dIndex) => (
                                  <motion.li
                                    key={dIndex}
                                    initial={{ opacity: 0, x: isLeft ? 20 : -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: dIndex * 0.08 }}
                                    className="flex items-start gap-2 text-sm text-gray-700"
                                  >
                                    <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                                    <span>{detail}</span>
                                  </motion.li>
                                ))}
                              </ul>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>

                  {/* Center Node */}
                  <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 z-10">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={inView ? { scale: 1 } : {}}
                      transition={{ ...springTransition, delay: index * 0.12 + 0.2 }}
                      className="relative"
                    >
                      <motion.div
                        animate={isHovered || isExpanded ? { scale: 1.2 } : { scale: 1 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                        className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shadow-md ${
                          isLast ? 'bg-accent-500' : isExpanded ? 'bg-primary-600' : 'bg-white border-2 border-primary-400'
                        }`}
                      >
                        <i className={`${stage.icon} text-lg md:text-xl ${
                          isLast ? 'text-white' : isExpanded ? 'text-white' : 'text-primary-600'
                        }`}></i>
                      </motion.div>

                      {/* Pulse ring animation */}
                      {(isHovered || isExpanded) && (
                        <motion.div
                          initial={{ scale: 1, opacity: 0.6 }}
                          animate={{ scale: 1.6, opacity: 0 }}
                          transition={{ duration: 1, repeat: Infinity }}
                          className={`absolute inset-0 rounded-full ${
                            isLast ? 'bg-accent-500' : 'bg-primary-400'
                          }`}
                        ></motion.div>
                      )}
                    </motion.div>
                  </div>

                  {/* Mobile/Tablet Node */}
                  <div className="lg:hidden absolute left-6 md:left-8 -translate-x-1/2 z-10">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={inView ? { scale: 1 } : {}}
                      transition={{ ...springTransition, delay: index * 0.12 + 0.2 }}
                      className="relative"
                    >
                      <motion.div
                        animate={isHovered || isExpanded ? { scale: 1.15 } : { scale: 1 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center shadow-md ${
                          isLast ? 'bg-accent-500' : isExpanded ? 'bg-primary-600' : 'bg-white border-2 border-primary-400'
                        }`}
                      >
                        <i className={`${stage.icon} text-base md:text-lg ${
                          isLast ? 'text-white' : isExpanded ? 'text-white' : 'text-primary-600'
                        }`}></i>
                      </motion.div>
                    </motion.div>
                  </div>

                  {/* Spacer for opposite side */}
                  <div className="hidden lg:block flex-1 w-5/12"></div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.2, ...springTransition }}
          className="mt-12 md:mt-16 flex flex-col items-center text-center"
        >
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 md:p-8 max-w-2xl w-full">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50">
                <i className="ri-calendar-check-line text-xl text-primary-600"></i>
              </div>
              <span className="text-sm font-semibold text-navy-900">Average time to first engineer: <span className="text-primary-600">14 days</span></span>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Some engagements move faster. Urgent needs? We have pre-vetted engineers ready to start within 48 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  const el = document.getElementById('cta');
                  if (el) {
                    const offset = 80;
                    const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                    window.scrollTo({ top: pos, behavior: 'smooth' });
                  }
                }}
                className="px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Start Your Timeline
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById('process');
                  if (el) {
                    const offset = 80;
                    const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                    window.scrollTo({ top: pos, behavior: 'smooth' });
                  }
                }}
                className="px-6 py-3 bg-white text-navy-900 text-sm font-semibold rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                View Hiring Process
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}